import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Loader2 } from "lucide-react";
import SEO, { SchemaMarkup } from "@/components/SEO";
import { CalendlyModal, CALENDLY_URLS } from "@/components/CalendlyModal";
import { useSiteContent } from "@/hooks/useSiteContent";
import GoogleReview, { GOOGLE_REVIEWS, GoogleVerifiedBadge } from "@/components/GoogleReview";
import ReadTimeSignal from "@/components/ReadTimeSignal";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const HERO_IMAGE = "/images/hero-desktop.webp";
const HERO_DESKTOP_WEBP = "/images/hero-desktop.webp";
const HERO_MOBILE_WEBP = "/images/hero-mobile.webp";
const HERO_MOBILE_JPG = "/images/hero-mobile.jpg";
const BROOKLYN_IMAGE = "/images/be/be-02.jpg";

const PRESSURE_SYMPTOMS = [
  { title: "Decisions you'd reverse", desc: "Made under pressure, they feel certain. Later, you see what you missed." },
  { title: "Conversations that escalate", desc: "Tone shifts before you catch it. What should resolve, compounds." },
  { title: "Patterns that repeat", desc: "Despite insight, despite effort, the same response under the same conditions." },
  { title: "Performance that costs more than it should", desc: "You're capable, but maintaining it under sustained pressure takes more than it used to." },
];

const OUTCOMES = [
  { title: "Decisions feel like theirs again", desc: "The second-guessing stops. Confidence in judgment returns, not because circumstances changed, but because access was restored." },
  { title: "Performance under pressure requires less effort", desc: "The exhausting work of managing yourself under load decreases. The architecture does the work that willpower was doing." },
  { title: "Outcomes arrive faster than the timeline suggested", desc: "When the subconscious obstruction is removed, progress that felt stuck begins moving, often within days of a session." },
];

const FAQ_ITEMS = [
  { q: "What is stress architecture?", a: "Stress architecture refers to the automatic patterns your brain runs under pressure, the unconscious responses that drive your decisions, reactions, and behaviour when stress hits. Most people try to manage stress at the surface. We identify and rewire the pattern underneath." },
  { q: "Who is Innate Perspectives for?", a: "Executives, founders, and high-performers who are functionally successful but notice stress, reactivity, or decision fatigue quietly costing them, in their leadership, relationships, or health." },
  { q: "How is this different from standard coaching or therapy?", a: "Standard coaching works at the level of goals and behaviour. Therapy often focuses on the past. Innate Perspectives works at the level of the subconscious pattern driving both, using CBT, NLP, and Psych-K to create change that actually holds under pressure." },
  { q: "What is the free diagnostic?", a: "A 7-minute assessment that maps your personal stress stress default pattern, how you specifically respond when pressure peaks. You'll receive a personalised report immediately after completing it." },
  { q: "How quickly can I expect results?", a: "Most clients notice a measurable shift within the first 2–3 sessions. The diagnostic helps identify exactly where to focus so the work is precise, not general." },
  { q: "Do you work with individuals or teams?", a: "Both. Brooklyn works with individual executives one-on-one and also runs stress architecture programs for leadership teams." },
  { q: "Where are sessions held?", a: "Sessions are conducted online via video call, making it accessible to executives across Australia and internationally." },
];

/* Reusable editorial bits */
const Eyebrow = ({ children, dark = false }) => (
  <div
    className={`inline-flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] uppercase mb-5 ${
      dark ? "text-gold-light" : "text-gold"
    }`}
  >
    <span className={`block w-[22px] h-px ${dark ? "bg-gold-light" : "bg-gold"}`} aria-hidden="true" />
    <span>{children}</span>
  </div>
);

const GoldRule = () => (
  <div className="flex justify-center py-14" aria-hidden="true">
    <span className="block w-16 h-px bg-gold" />
  </div>
);

export default function SharedLandingPage() {
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const siteContent = useSiteContent();
  const t = (key, fallback) => siteContent[key] || fallback;

  // Light mode always on this page for now (editorial aesthetic)
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  const initiateFocusSessionCheckout = async () => {
    setIsCheckoutLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/checkout/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ package_id: "focused_session", origin_url: window.location.origin }),
      });
      const data = await response.json();
      if (data.url) window.location.href = data.url;
    } catch (error) {
      console.error("Checkout error:", error);
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream text-ink font-sans font-light" data-testid="home-landing-page">
      <SEO
        title="Stress Response Patterns | Free 7-Min Assessment"
        description="Understand your stress response patterns and rewire the automatic decisions hijacking your performance under pressure. Free 7-minute diagnostic."
        path="/"
      />
      <SchemaMarkup />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ_ITEMS.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          })}
        </script>
      </Helmet>

      {/* HERO, wide editorial: subject anchored right, headline lives on the clean cream wall on the left */}
      <section
        className="relative min-h-[95vh] landscape:min-h-[100vh] flex items-end md:items-center overflow-hidden bg-cream"
        aria-label="Hero"
      >
        <div className="absolute inset-0 z-0">
          <picture className="md:hidden w-full h-full block">
            <source type="image/webp" srcSet={HERO_MOBILE_WEBP} />
            <img
              src={HERO_MOBILE_JPG}
              alt="Brooklyn Eller, founder of Innate Perspectives, at her desk with laptop and notebook, warmly smiling"
              className="w-full h-full object-cover"
              style={{ objectPosition: "center 20%", filter: "contrast(1.02) saturate(0.94)" }}
              loading="eager"
              fetchPriority="high"
              width="768"
              height="510"
              decoding="async"
            />
          </picture>
          <picture className="hidden md:block w-full h-full">
            <source type="image/webp" srcSet={HERO_DESKTOP_WEBP} />
            <img
              src={HERO_IMAGE}
              alt="Brooklyn Eller, founder of Innate Perspectives, at her desk with laptop and notebook, warmly smiling"
              className="w-full h-full object-cover"
              style={{ objectPosition: "center center", filter: "contrast(1.02) saturate(0.98) brightness(1.02)" }}
              loading="eager"
              fetchPriority="high"
              width="1264"
              height="841"
              decoding="async"
            />
          </picture>
        </div>

        {/* Mobile vignette, softer top, stronger dark band where headline + CTA sit */}
        <div
          className="absolute inset-0 z-[1] pointer-events-none md:hidden"
          style={{
            background:
              "linear-gradient(to bottom, rgba(28,21,16,0.10) 0%, rgba(28,21,16,0.32) 28%, rgba(28,21,16,0.78) 58%, rgba(28,21,16,0.92) 100%)",
          }}
        />
        {/* Desktop: dark ink wash on the left so cream text stays crisp over the
            office interior. Softened right-side so Brooklyn's face + blazer read brighter. */}
        <div
          className="absolute inset-0 z-[1] pointer-events-none hidden md:block"
          style={{
            background:
              "linear-gradient(90deg, rgba(28,21,16,0.80) 0%, rgba(28,21,16,0.62) 28%, rgba(28,21,16,0.32) 45%, rgba(28,21,16,0.06) 58%, rgba(28,21,16,0) 70%)",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-8 md:px-12 lg:px-16 w-full pt-8 sm:pt-12 md:pt-0 pb-14 md:pb-0 landscape:pb-6">
          <div className="relative w-full md:max-w-[44%] lg:max-w-[48%] xl:max-w-[50%] text-center md:text-left mx-auto md:mx-0">
            <div className="inline-flex items-center gap-3 font-mono font-semibold text-[12px] sm:text-[12px] md:text-[10px] tracking-[0.3em] uppercase mb-6 md:mb-8 text-gold-light">
              <span className="block w-[28px] h-px bg-current" aria-hidden="true" />
              <span>Stress Decision Architecture™</span>
            </div>
            <h1 className="font-editorial font-light text-[36px] sm:text-[44px] md:text-[38px] lg:text-[52px] xl:text-[64px] landscape:text-[24px] landscape:sm:text-[32px] landscape:md:text-[40px] text-cream leading-[1.05] mb-5 sm:mb-6 landscape:mb-3">
              {t("landing.hero.headline_main", "Map your stress")}
              <br />
              <em className="not-italic italic font-semibold text-gold-light">
                {t("landing.hero.headline_italic", "response pattern.")}
              </em>
            </h1>
            <p className="font-editorial italic text-cream/85 text-[20px] sm:text-[22px] md:text-[20px] lg:text-[24px] xl:text-[26px] leading-[1.3] mb-8 sm:mb-10 landscape:mb-4 max-w-[460px] mx-auto md:mx-0">
              Seven minutes. Eleven scenarios. One pattern that's been running the show.
            </p>

            <p className="text-cream/90 text-[18px] sm:text-[18px] md:text-[15px] lg:text-[16px] leading-relaxed mb-7 sm:mb-9 landscape:mb-3 max-w-[420px] mx-auto md:mx-0">
              {t(
                "landing.hero.subhead",
                "Most leaders don't fail from lack of intelligence, they lose access to clarity in your clarity under pressure. Find the pattern driving your automatic responses."
              )}
            </p>

            <div className="flex justify-center md:justify-start">
              <Link
                to="/quiz?start=true"
                data-testid="hero-quiz-cta"
                aria-label="Take the free stress response patterns assessment"
                className="inline-flex items-center gap-3 bg-cream text-ink font-mono text-[12px] sm:text-[12px] md:text-[11px] tracking-[0.22em] uppercase px-7 sm:px-9 py-4 sm:py-[18px] hover:bg-gold hover:text-cream hover:gap-[18px] transition-all duration-300"
              >
                {t("landing.hero.cta_text", "Map Your Pattern")}
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </div>
            <p className="font-mono text-[12px] sm:text-[12px] md:text-[11px] tracking-[0.08em] text-cream/80 mt-4 sm:mt-5 landscape:mt-2">
              {t(
                "landing.hero.microcopy",
                "Free · 7 minutes · Immediate results · No email required"
              )}
            </p>
          </div>
        </div>
      </section>

      <main id="main-content" role="main">
        {/* PROBLEM, editorial article block */}
        <section
          className="py-24 md:py-32 bg-cream"
          aria-labelledby="problem-heading"
        >
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <Eyebrow>Understanding Stress Response Patterns</Eyebrow>
            <h2
              id="problem-heading"
              className="font-editorial text-[32px] md:text-[44px] lg:text-[52px] font-light leading-[1.1] mb-6 text-ink"
            >
              Your stress response pattern
              <br />
              is <em className="not-italic italic font-light text-gold">running the show.</em>
            </h2>
            <ReadTimeSignal words={170} prefix="Essay" className="mb-8" testId="home-problem-read-time" />
            <div className="w-12 h-px bg-gold mb-10" aria-hidden="true" />

            <div className="space-y-5 text-[16px] md:text-[17px] leading-[1.85] text-ink/75 font-light">
              <p>
                Research shows <strong className="font-medium text-ink">77% of people get physical symptoms from stress.</strong> Most never see the pattern behind it. Your stress response runs on its own. It shapes every decision you make under pressure.
              </p>
              <p>
                When stress hits, your brain switches mode. Protection takes over. Clear thinking drops. You react before you can think. The response that runs is rarely the one you would choose.
              </p>
              <p>
                Most coaching works at the level of ideas. It gives you understanding. But your stress pattern runs below that. Awareness alone does not change it.
              </p>
            </div>

            {/* Gold-bordered pull quote */}
            <div className="mt-12 pl-8 py-6 border-l-[3px] border-gold bg-linen -ml-8 md:-ml-0 md:pl-10 md:py-8">
              <p className="font-editorial italic text-[19px] md:text-[22px] leading-[1.5] text-ink font-light">
                Stress Decision Architecture™ maps your exact pattern. Then it rewires the level where the pattern actually runs.
                <strong className="not-italic font-medium text-gold block mt-3">That is why the change holds.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS. 3-column editorial */}
        <section className="py-24 md:py-32 bg-warm border-t border-rule" aria-labelledby="how-it-works-heading">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="max-w-2xl mb-16">
              <Eyebrow>How It Works</Eyebrow>
              <h2
                id="how-it-works-heading"
                className="font-editorial text-[32px] md:text-[44px] font-light leading-[1.12] text-ink"
              >
                A simple path.
                <br />
                <em className="not-italic italic font-light text-gold">Built for busy leaders.</em>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-[2px] bg-rule border border-rule" role="list">
              {[
                { n: "Step 01", t: "Take the 7-minute diagnostic", d: "Answer 11 real leadership scenarios. Each one maps how you respond when pressure rises. You get your results right away. No email required." },
                { n: "Step 02", t: "See your stress default pattern", d: "Your report shows the one pattern that runs when stress peaks. It names where your access to clarity to clarity narrows first. It shows what drives the reactions you don't choose." },
                { n: "Step 03", t: "Rewire it with Brooklyn", d: "In a 45-minute session, we work at the level where the pattern actually runs. Clients notice a measurable shift within the first 2–3 sessions. The change holds because it is structural, not behavioural." },
              ].map((s) => (
                <article
                  key={s.n}
                  role="listitem"
                  className="group relative bg-cream p-9 md:p-10 hover:bg-linen transition-colors duration-300"
                >
                  <span
                    className="absolute left-0 top-0 bottom-0 w-[3px] bg-transparent group-hover:bg-gold transition-colors duration-300"
                    aria-hidden="true"
                  />
                  <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-gold mb-3">
                    {s.n}
                  </div>
                  <h3 className="font-editorial text-[22px] font-semibold text-ink mb-3 leading-tight">
                    {s.t}
                  </h3>
                  <p className="text-[14px] leading-[1.75] text-ink/70 font-light">{s.d}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* PRESSURE SYMPTOMS, editorial 4-up on ink */}
        <section className="py-24 md:py-32 bg-ink" aria-labelledby="symptoms-heading">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="max-w-2xl mb-14">
              <Eyebrow dark>The Performance Tax</Eyebrow>
              <h2
                id="symptoms-heading"
                className="font-editorial text-[32px] md:text-[44px] font-light leading-[1.12] text-warm"
              >
                What it costs <em className="not-italic italic font-bold text-gold-light">before you notice</em>
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1px] bg-gold-dim border border-gold-dim" role="list">
              {PRESSURE_SYMPTOMS.map((item, i) => (
                <article
                  key={i}
                  className="bg-ink p-8 md:p-10"
                  role="listitem"
                >
                  <div className="flex items-start gap-4">
                    <div className="font-mono text-[10px] tracking-[0.25em] text-gold mt-1 min-w-[36px]">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="font-editorial text-[20px] md:text-[22px] font-semibold text-warm mb-2 leading-tight">
                        {item.title}
                      </h3>
                      <p className="text-[14px] leading-[1.7] text-warm/65 font-light">{item.desc}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <p className="font-editorial italic text-[18px] md:text-[20px] text-warm/75 mt-14 max-w-2xl leading-[1.55]">
              That's not a character flaw. That's stress architecture operating exactly as it was designed to. <span className="text-gold-light">just not in your service.</span>
            </p>
          </div>
        </section>

        {/* DIFFERENCE. Brooklyn's distinction */}
        <section className="py-24 md:py-32 bg-cream" aria-labelledby="difference-heading">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <Eyebrow>The Distinction</Eyebrow>
            <h2
              id="difference-heading"
              className="font-editorial text-[32px] md:text-[44px] font-light leading-[1.1] mb-10 text-ink"
            >
              What Brooklyn sees
              <br />
              <em className="not-italic italic font-light text-gold">that others don't.</em>
            </h2>

            <div className="space-y-5 text-[16px] md:text-[17px] leading-[1.85] text-ink/75 font-light mb-10">
              <p>
                Most practitioners work with what's visible, behaviour, belief, reported experience. Brooklyn works with what's underneath it.
              </p>
            </div>

            <div className="border border-rule bg-warm p-8 md:p-12 mb-10">
              <p className="font-editorial italic text-[19px] md:text-[22px] leading-[1.55] text-ink font-light">
                "Your current challenges aren't random. I trace them back to the root cause, the subconscious drivers and outdated protective strategies shaping your decisions and limiting your results."
              </p>
            </div>

            <div className="space-y-5 text-[16px] md:text-[17px] leading-[1.85] text-ink/75 font-light">
              <p>
                By examining live challenges in real time, Brooklyn identifies precisely where stress is reallocating access and which specific architecture is creating the obstruction.
              </p>
              <p>
                This is not pattern matching from a framework. It is direct perception, developed across years of combining Kolbe methodology, neuro change methods, CBT, NLP, and Psych-K into a proprietary process that creates change at the structural level, not the surface.
              </p>
            </div>

            <div className="mt-10 pt-8 border-t border-rule">
              <p className="font-editorial text-[20px] md:text-[24px] leading-[1.35] text-ink font-light">
                The result: <strong className="font-semibold">change that doesn't require effort to maintain.</strong>
                <span className="block mt-2 text-ink/65 italic font-light text-[17px] md:text-[19px]">
                  Because it's installed at the level where the pattern was running, not managed on top of it.
                </span>
              </p>
            </div>
          </div>
        </section>

        {/* OUTCOMES */}
        <section className="py-24 md:py-32 bg-linen" aria-labelledby="outcomes-heading">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="max-w-2xl mb-14">
              <Eyebrow>What Leaders Experience</Eyebrow>
              <h2
                id="outcomes-heading"
                className="font-editorial text-[32px] md:text-[44px] font-light leading-[1.12] text-ink"
              >
                The outcomes arrive
                <br />
                <em className="not-italic italic font-light text-gold">faster than expected.</em>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {OUTCOMES.map((o, i) => (
                <article key={i} className="relative">
                  <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-gold mb-3">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-editorial text-[22px] md:text-[24px] font-semibold text-ink mb-3 leading-[1.2]">
                    {o.title}
                  </h3>
                  <p className="text-[14.5px] leading-[1.75] text-ink/70 font-light">{o.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS. Real Google reviews */}
        <section className="py-24 md:py-32 bg-cream" aria-labelledby="testimonials-heading">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="max-w-3xl mb-14">
              <Eyebrow>What Clients Say</Eyebrow>
              <h2
                id="testimonials-heading"
                className="font-editorial text-[32px] md:text-[44px] font-light leading-[1.1] mb-5 text-ink"
              >
                What changes
                <br />
                <em className="not-italic italic font-light text-gold">when access is restored.</em>
              </h2>
              <div className="mt-6">
                <GoogleVerifiedBadge />
              </div>
            </div>

            <div className="max-w-2xl mx-auto grid grid-cols-1 gap-6 md:gap-8">
              {GOOGLE_REVIEWS.filter(r => ["leanne-druhan", "matt-hall", "keane-marsh", "daryl-seager"].includes(r.id)).map((r, i) => (
                <GoogleReview key={r.id} review={r} testId={`home-google-review-${i}`} />
              ))}
            </div>

            <div className="mt-7 text-center">
              <a
                href="https://www.google.com/search?q=Innate+Perspectives+Brooklyn+Eller+reviews"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-ink/60 hover:text-gold transition-colors"
                data-testid="home-view-all-google-reviews"
              >
                View all reviews on Google
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M7 17L17 7M17 7H7M17 7V17" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        {/* CHOOSE YOUR DEPTH */}
        <section className="py-24 md:py-32 bg-warm border-t border-rule" aria-labelledby="depth-heading">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="max-w-2xl mb-14">
              <Eyebrow>Choose Your Depth of Work</Eyebrow>
              <h2
                id="depth-heading"
                className="font-editorial text-[32px] md:text-[44px] font-light leading-[1.1] text-ink"
              >
                From first clarity
                <br />
                <em className="not-italic italic font-light text-gold">to full architecture.</em>
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-[1px] bg-rule border border-rule">
              {/* Diagnostic - Free */}
              <div className="bg-cream p-8 md:p-10 flex flex-col">
                <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-gold mb-3">
                  Diagnostic · Free
                </p>
                <h3 className="font-editorial text-[24px] font-semibold text-ink mb-2 leading-tight">
                  Stress Architecture Diagnostic
                </h3>
                <p className="font-mono text-[10px] tracking-[0.1em] text-ash mb-5">
                  FREE · 7 MINUTES · IMMEDIATE
                </p>
                <p className="text-[14.5px] leading-[1.75] text-ink/70 font-light mb-8 flex-grow">
                  Map your stress default pattern and see your personal stress pattern clearly for the first time.
                </p>
                <Link to="/quiz?start=true" data-testid="depth-diagnostic-cta" className="inline-flex items-center justify-center gap-3 bg-ink text-cream font-mono text-[10px] tracking-[0.22em] uppercase px-6 py-4 hover:bg-gold hover:gap-[18px] transition-all duration-300 w-full">
                  Take the Diagnostic <ArrowRight size={13} />
                </Link>
              </div>

              {/* Focus Session */}
              <div className="bg-cream p-8 md:p-10 flex flex-col">
                <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-gold mb-3">
                  Individual · One Session
                </p>
                <h3 className="font-editorial text-[24px] font-semibold text-ink mb-2 leading-tight">
                  Focus Session
                </h3>
                <p className="font-mono text-[10px] tracking-[0.1em] text-ash mb-5">
                  45 MINUTES · $150
                </p>
                <p className="text-[14.5px] leading-[1.75] text-ink/70 font-light mb-8 flex-grow">
                  One live challenge. One structural recalibration. Applied directly to what's in front of you right now.
                </p>
                <button
                  onClick={initiateFocusSessionCheckout}
                  disabled={isCheckoutLoading}
                  data-testid="depth-focus-session-cta"
                  className="inline-flex items-center justify-center gap-3 bg-transparent text-ink border border-ink font-mono text-[10px] tracking-[0.22em] uppercase px-6 py-4 hover:bg-ink hover:text-cream hover:gap-[18px] transition-all duration-300 w-full disabled:opacity-50"
                >
                  {isCheckoutLoading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Processing…
                    </>
                  ) : (
                    <>
                      Book a Focus Session <ArrowRight size={13} />
                    </>
                  )}
                </button>
              </div>

              {/* Ten-Session Programme, dark variant */}
              <div className="bg-ink p-8 md:p-10 flex flex-col relative">
                <div
                  className="absolute top-4 right-4 font-mono text-[9px] tracking-[0.25em] uppercase text-gold-light border border-gold-light px-2 py-1"
                  aria-hidden="true"
                >
                  Deepest
                </div>
                <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-gold-light mb-3">
                  Individual · Ten Sessions
                </p>
                <h3 className="font-editorial text-[24px] font-semibold text-warm mb-2 leading-tight">
                  Stress Decision Architecture™
                </h3>
                <p className="font-mono text-[10px] tracking-[0.1em] text-warm/60 mb-5">
                  10 × 60 MINUTES · $3,500
                </p>
                <p className="text-[14.5px] leading-[1.75] text-warm/75 font-light mb-8 flex-grow">
                  A complete architecture rewrite. Full mapping, capacity installation, and sustained integration across ten sessions, for leaders who want the pattern resolved at the root.
                </p>
                <Link to="/leadership/services/10-session-program" data-testid="depth-ten-session-cta" className="inline-flex items-center justify-center gap-3 bg-cream text-ink font-mono text-[10px] tracking-[0.22em] uppercase px-6 py-4 hover:bg-gold hover:text-cream hover:gap-[18px] transition-all duration-300 w-full">
                  View the Programme <ArrowRight size={13} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* YOUR GUIDE, editorial split */}
        <section className="py-24 md:py-32 bg-cream border-t border-rule" aria-labelledby="guide-heading">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-start">
              <div className="relative pb-6 lg:pb-0">
                <div
                  className="absolute -inset-6 border border-rule-gold -z-10"
                  aria-hidden="true"
                />
                <img
                  src={BROOKLYN_IMAGE}
                  alt="Brooklyn Eller. Executive stress and decision-making coach"
                  className="w-full h-auto object-cover"
                  style={{ filter: "contrast(1.03) saturate(0.9)" }}
                  loading="lazy"
                />
                <div
                  className="absolute -bottom-2 right-4 bg-ink text-cream font-mono text-[9px] tracking-[0.22em] uppercase px-4 py-[10px]"
                  aria-hidden="true"
                >
                  Brooklyn Eller · Founder
                </div>
              </div>

              <div>
                <Eyebrow>Your Guide</Eyebrow>
                <h2
                  id="guide-heading"
                  className="font-editorial text-[32px] md:text-[40px] font-light leading-[1.12] text-ink mb-8"
                >
                  Who holds
                  <br />
                  <em className="not-italic italic font-light text-gold">the work.</em>
                </h2>

                <p className="text-[17px] md:text-[18px] leading-[1.7] text-ink font-medium mb-6">
                  {t(
                    "landing.bio.headline",
                    "Most high performers don't have a strategy problem. They have a pattern problem."
                  )}
                </p>

                <div className="space-y-4 text-[15px] md:text-[16px] leading-[1.85] text-ink/75 font-light">
                  <p>
                    {t(
                      "landing.bio.paragraph",
                      "After 20 years working at the intersection of neuroscience, decision-making, and leadership, one thing is consistent: the executives and founders who hit ceilings, through burnout, self-sabotage, or chronic misreading of the room, aren't lacking intelligence or drive. They're running an autopilot their nervous system built before they had any say in the matter."
                    )}
                  </p>
                  <p className="font-medium text-ink">
                    {t("landing.bio.emphasis", "That's the problem Brooklyn solves.")}
                  </p>
                  <p>
                    {t(
                      "landing.bio.closing",
                      "Through Stress Decision Architecture™, Brooklyn maps the specific pattern your nervous system defaults to under pressure, and builds the structural conditions for you to lead, decide, and communicate from your actual capability, not your threat response."
                    )}
                  </p>
                </div>

                <p className="font-editorial italic text-[18px] md:text-[20px] text-gold mt-8 leading-snug">
                  This isn't mindset work. It's architecture.
                </p>

                <div className="mt-8 flex flex-wrap gap-[6px]">
                  {["Kolbe Certified", "Neuro Change Methods", "CBT", "NLP", "Psych-K", "20+ Years"].map((c) => (
                    <span
                      key={c}
                      className="font-mono text-[9px] tracking-[0.16em] uppercase px-3 py-2 border border-rule text-ash"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-24 md:py-32 bg-warm border-t border-rule" aria-labelledby="faq-heading">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <Eyebrow>Frequently Asked</Eyebrow>
            <h2
              id="faq-heading"
              className="font-editorial text-[32px] md:text-[44px] font-light leading-[1.1] mb-4 text-ink"
            >
              Questions leaders ask
              <br />
              <em className="not-italic italic font-light text-gold">before they map their pattern.</em>
            </h2>
            <p className="text-[15px] md:text-[16px] leading-[1.75] text-ink/65 font-light mb-10 max-w-2xl">
              A plain-language introduction to Stress Decision Architecture™, the five archetypes, and how the 7-minute assessment differs from traditional leadership coaching or therapy.
            </p>

            <div className="divide-y divide-rule border-t border-b border-rule" data-testid="faq-list">
              {FAQ_ITEMS.map((item, i) => (
                <details
                  key={i}
                  className="group py-5 md:py-6"
                  data-testid={`faq-item-${i + 1}`}
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-editorial text-[19px] md:text-[22px] leading-tight text-ink font-medium">
                    <span>{item.q}</span>
                    <span
                      className="mt-1 font-mono text-[14px] leading-none transition-transform duration-300 group-open:rotate-45 text-gold shrink-0"
                      aria-hidden="true"
                    >
                      +
                    </span>
                  </summary>
                  <div className="mt-4 text-[14.5px] md:text-[15.5px] leading-[1.85] text-ink/70 font-light max-w-2xl">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>

            <p className="text-[14px] text-ink/60 mt-10 font-light">
              Still have a question?{" "}
              <a
                href="mailto:hello@innateperspectives.com"
                className="text-gold hover:text-ink underline underline-offset-4 transition-colors"
              >
                Email Brooklyn directly →
              </a>
            </p>
          </div>
        </section>

        {/* FINAL CTA */}
        <section
          className="relative py-24 md:py-36 bg-ink overflow-hidden"
          aria-labelledby="final-cta-heading"
        >
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative z-10">
            <div className="inline-flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] uppercase mb-6 text-gold-light">
              <span className="block w-[22px] h-px bg-gold-light" aria-hidden="true" />
              <span>Start Here</span>
              <span className="block w-[22px] h-px bg-gold-light" aria-hidden="true" />
            </div>
            <h2
              id="final-cta-heading"
              className="font-editorial text-[36px] md:text-[56px] font-light leading-[1.08] mb-6 text-warm"
            >
              {t("landing.final_cta.title_main", "Clarity doesn't disappear.")}
              <br />
              <em className="not-italic italic font-light text-gold-light">
                {t("landing.final_cta.title_italic", "Access to clarity does.")}
              </em>
            </h2>
            <p className="font-editorial italic text-[19px] md:text-[24px] text-warm/75 mb-10 font-light">
              {t("landing.final_cta.subhead", "Map your architecture in 7 minutes.")}
            </p>

            <Link
              to="/quiz?start=true"
              data-testid="final-quiz-cta"
              className="inline-flex items-center gap-3 bg-cream text-ink font-mono text-[11px] tracking-[0.22em] uppercase px-10 py-[18px] hover:bg-gold hover:text-cream hover:gap-[18px] transition-all duration-300"
            >
              Take the Free Diagnostic <ArrowRight size={14} />
            </Link>
            <p className="font-mono text-[10px] tracking-[0.08em] text-warm/50 mt-4">
              Free · 7 minutes · No email required
            </p>

            <div className="mt-14 pt-10 border-t border-warm/10 flex flex-col sm:flex-row justify-center gap-8 text-[13px] font-mono tracking-[0.1em] uppercase">
              <Link to="/leadership/services" className="inline-flex items-center gap-2 text-gold-light hover:text-warm transition-colors">
                View Leadership Sessions <ArrowRight size={13} />
              </Link>
              <Link to="/workshops" className="inline-flex items-center gap-2 text-gold-light hover:text-warm transition-colors">
                View the Workshop <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <CalendlyModal
        isOpen={isCalendlyOpen}
        onClose={() => setIsCalendlyOpen(false)}
        calendlyUrl={CALENDLY_URLS.clarityCall}
        title="Book a Focus Session"
      />
    </div>
  );
}
